import { CloseButton } from '@orn/shared-web/components/Button';
import { CheckIcon, ClockIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import AlertBlockedNotice from '../alerts/AlertBlockedNotice';

export { BLOCKED_NOTICE_TEXT } from '../alerts/AlertBlockedNotice';

export type AlertListItemStatus = 'unread' | 'read' | 'pending' | 'resolved';

interface IAlertListItemProps {
  /** Leading visual, rendered at 40px — caller provides the logo image or tinted icon circle. */
  icon: React.ReactNode;
  /** Headline, truncated to a single line. */
  title: React.ReactNode;
  /**
   * Secondary text, clamped to two lines; callers embed emphasis spans inline.
   * The two-line space is reserved even when shorter or absent, so every row
   * renders at one standard height.
   */
  subtitle?: React.ReactNode;
  /** Preformatted compact timestamp — see `formatCompactTimeAgo` in shared-web. */
  timestamp?: string;
  /**
   * Status badge on the icon's corner: a dot when unread, a clock when pending,
   * a check when resolved. `read` shows nothing. @default 'read'
   */
  status?: AlertListItemStatus;
  /** 'error' renders the title in the error color. @default 'default' */
  tone?: 'default' | 'error';
  /** Highlighted border — the row currently injected into the assistant chat. */
  selected?: boolean;
  onClick?: () => void;
  /** Renders a dismiss X, revealed on hover/focus over the timestamp. Ignored on a `resolved` row. */
  onDismiss?: () => void;
  /**
   * Refusal feedback for a click the surface would not act on (the assistant
   * is mid-turn): dims the card to 30% and floats a lock pill over it at full
   * opacity, both fading. The row stays interactive — the click is what
   * produces this feedback — so the dismiss X keeps working.
   */
  showBlockedNotice?: boolean;
  className?: string;
}

/**
 * Vertical list row for the Alerts Center: icon + title + subtitle + compact
 * timestamp, and a status badge on the icon. Every row is one standard height
 * regardless of subtitle length. Purely presentational — callers own
 * data fetching, the status/tone mapping, and per-type icon choice.
 * When `onClick` is set the row is keyboard-actionable
 * (Enter/Space); `onDismiss` reveals an X over the timestamp on hover/focus,
 * except on a `resolved` row, which is already closed.
 */
const AlertListItem = ({
  icon,
  title,
  subtitle,
  timestamp,
  status = 'read',
  tone = 'default',
  selected = false,
  onClick,
  onDismiss,
  showBlockedNotice = false,
  className,
}: IAlertListItemProps) => {
  const titleId = React.useId();

  // A resolved alert is closed, so there is nothing left to dismiss — the row
  // keeps its timestamp and drops the X even where a surface hands it a handler.
  const dismiss = status === 'resolved' ? undefined : onDismiss;

  return (
    // The pill cannot live inside the card — it must stay at full opacity
    // while the card dims — so both hang off this neutral wrapper.
    <div className={cx('relative w-full', className)}>
      <div
        className={cx(
          'bg-primary shadow-card group relative flex w-full items-start gap-3 rounded-2xl border p-3',
          'transition-opacity duration-200',
          // The inset ring doubles the selected border to its 2px weight
          // without moving content — the border itself stays 1px in both states.
          selected ? 'border-brand_alt ring-border-brand_alt ring-1 ring-inset' : 'border-secondary_alt',
          onClick && 'hover:bg-primary_hover',
          showBlockedNotice && 'opacity-30'
        )}
      >
        {/*
         * The row action is an overlay button rather than a clickable root, so the
         * dismiss button stays its sibling — a control nested inside a button is
         * not reliably reachable for keyboard and screen-reader users.
         */}
        {onClick && (
          <button
            type="button"
            aria-labelledby={titleId}
            onClick={onClick}
            className="outline-focus-ring absolute inset-0 cursor-pointer rounded-2xl focus-visible:outline-2"
          />
        )}

        <div className="relative shrink-0">
          {icon}
          {/* The ring cuts the badge out of the icon beneath it, which is an arbitrary brand logo. */}
          {status === 'unread' && (
            <span className="bg-utility-brand-400 ring-bg-primary absolute -bottom-1 -right-1 size-3.5 rounded-full ring-2" />
          )}
          {status === 'pending' && (
            <span className="bg-utility-warning-400 ring-bg-primary absolute -bottom-1 -right-1 flex size-3.5 items-center justify-center rounded-full ring-2">
              <ClockIcon className="text-fg-white size-2.5 shrink-0" />
            </span>
          )}
          {status === 'resolved' && (
            <span className="bg-success-solid ring-bg-primary absolute -bottom-1 -right-1 flex size-3.5 items-center justify-center rounded-full ring-2">
              <CheckIcon className="text-fg-white size-2.5 shrink-0" />
            </span>
          )}
        </div>

        {/* No `overflow-hidden` here: the dismiss button sits above this column's
          top edge, and clipping the column clips the button. Overflow is cut on
          the title and subtitle themselves, which the button does not overlap. */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-start justify-between gap-3">
            <span
              id={titleId}
              className={cx(
                'text-md min-w-0 truncate font-semibold',
                tone === 'error' ? 'text-error-primary' : 'text-primary'
              )}
            >
              {title}
            </span>
            {(timestamp || dismiss) && (
              <span className="relative shrink-0">
                {timestamp && (
                  <span
                    className={cx(
                      'text-tertiary whitespace-nowrap text-sm',
                      dismiss && 'group-hover:opacity-0 group-has-[:focus-visible]:opacity-0'
                    )}
                  >
                    {timestamp}
                  </span>
                )}
                {dismiss && (
                  <span
                    className={cx(
                      // `:focus-visible`, not `:focus-within` — a mouse click leaves
                      // DOM focus on the button, which would pin the X open and keep
                      // the timestamp hidden long after the pointer has left.
                      // z-10 lifts the button above the row's overlay action.
                      'z-10 opacity-0 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100',
                      timestamp ? 'absolute -top-1 right-0' : 'relative'
                    )}
                  >
                    <CloseButton
                      size="xs"
                      label="Dismiss alert"
                      onPress={dismiss}
                      className="bg-tertiary rounded-full"
                    />
                  </span>
                )}
              </span>
            )}
          </div>
          {/* min-h-[2lh] holds both subtitle lines even when empty — one standard row height. */}
          <div className="text-secondary line-clamp-2 min-h-[2lh] break-words text-sm">{subtitle}</div>
        </div>
      </div>

      <AlertBlockedNotice show={showBlockedNotice} />
    </div>
  );
};

export default AlertListItem;
